import { useState, useEffect } from 'react';

interface FloatingShareBarProps {
  title: string;
  url?: string;
}

export default function FloatingShareBar({ title, url }: FloatingShareBarProps) {
  const [visible, setVisible] = useState(false);
  const [copied, setCopied] = useState(false);

  const shareUrl = url ?? window.location.href;
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 320);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
    } catch {
      const el = document.createElement('textarea');
      el.value = shareUrl;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const items = [
    {
      label: 'Share on X',
      icon: 'ri-twitter-x-line',
      href: `https://twitter.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
      hoverClass: 'hover:bg-stone-900 hover:text-white hover:border-stone-900',
    },
    {
      label: 'Pin on Pinterest',
      icon: 'ri-pinterest-line',
      href: `https://pinterest.com/pin/create/button/?url=${encodedUrl}&description=${encodedTitle}`,
      hoverClass: 'hover:bg-red-600 hover:text-white hover:border-red-600',
    },
    {
      label: 'Share on Facebook',
      icon: 'ri-facebook-line',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      hoverClass: 'hover:bg-rose-600 hover:text-white hover:border-rose-600',
    },
  ];

  return (
    /* Only render on lg+ screens via hidden lg:flex */
    <div
      className={`hidden lg:flex fixed left-6 top-1/2 -translate-y-1/2 z-40 flex-col items-center gap-2 transition-all duration-300 ${
        visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6 pointer-events-none'
      }`}
    >
      {/* "Share" label rotated */}
      <span
        className="text-xs font-semibold text-stone-400 uppercase tracking-widest mb-1 select-none"
        style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)', letterSpacing: '0.15em' }}
      >
        Share
      </span>

      {/* Divider */}
      <div className="w-px h-6 bg-stone-200" />

      {/* Social buttons */}
      {items.map(({ label, icon, href, hoverClass }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="nofollow noopener noreferrer"
          aria-label={label}
          title={label}
          className={`group w-9 h-9 flex items-center justify-center rounded-full border border-stone-200 bg-white text-stone-500 transition-all duration-200 cursor-pointer ${hoverClass}`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${icon} text-base`} />
          </div>
        </a>
      ))}

      {/* Copy link */}
      <button
        onClick={handleCopy}
        aria-label="Copy link"
        title={copied ? 'Copied!' : 'Copy link'}
        className={`w-9 h-9 flex items-center justify-center rounded-full border transition-all duration-200 cursor-pointer ${
          copied
            ? 'bg-rose-500 text-white border-rose-500'
            : 'border-stone-200 bg-white text-stone-500 hover:bg-rose-500 hover:text-white hover:border-rose-500'
        }`}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <i className={`${copied ? 'ri-check-line' : 'ri-link'} text-base`} />
        </div>
      </button>

      {/* Tooltip for copy confirmation */}
      {copied && (
        <span className="absolute left-12 bg-stone-800 text-white text-xs px-2 py-1 rounded-md whitespace-nowrap pointer-events-none">
          Copied!
        </span>
      )}

      {/* Divider */}
      <div className="w-px h-6 bg-stone-200 mt-1" />
    </div>
  );
}
